// Standardized marketing/funnel events pushed to GTM → GA4 via window.dataLayer.
// GTM is injected in components/shared/GtmScript.tsx (only on godetour.dev), which
// creates window.dataLayer. Off that host the push just buffers into the array, so
// calling this anywhere is safe.
type StandardEvent =
  | "registration_started"
  | "registration_finished"
  | "app_created"
  | "purchase";

// `(string & {})` keeps the StandardEvent suggestions in autocomplete while still
// allowing an arbitrary custom (freeform) event name.
export type GtmEvent = StandardEvent | (string & {});

type GtmEventParams = {
  registration_started: Record<string, never>;
  registration_finished: { method?: "email" | "google" | "github" };
  app_created: Record<string, never>;
  purchase: {
    plan: "starter" | "scale" | "enterprise";
    interval?: "monthly" | "annual";
  };
};

declare global {
  interface Window {
    dataLayer?: Array<{ event: string } & Record<string, unknown>>;
  }
}

/**
 * Emits an event to GTM → GA4 (window.dataLayer).
 *
 * Standardized events and their parameters are documented in
 * `detour-platform/docs/analytics-events.md`. A custom (freeform) event name is
 * allowed too — use it deliberately, outside the standardized set.
 */
export function pushGtmEvent<E extends GtmEvent>(
  event: E,
  params?: E extends keyof GtmEventParams
    ? GtmEventParams[E]
    : Record<string, unknown>
) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...(params ?? {}) });
}
